/*
 * assets/exos-pdp-v2.js
 * Exos Door PDP v2 - display-only behavior layer.
 *
 * Loads with defer AFTER assets/door-configurator.js, only on product pages
 * using template suffix "door-builder".
 *
 * HARD RULES (2026-07-13 redesign contract):
 *   - Zero changes to cart payloads, wizard mechanics or validation.
 *   - This file only OBSERVES the existing configurator's DOM/state and
 *     enhances the display. It never writes into wizard state, never
 *     selects chips, never edits form inputs, never computes prices
 *     itself (it mirrors what the summary/button already display).
 *   - Every feature is independently fail-safe: an exception during a
 *     feature's init disables that feature only; the page keeps working
 *     exactly as it does without this file.
 *
 * Features:
 *   F1 outcome   - drives the [data-exos-outcome] "What your door arrives
 *                  with" rows by mirroring the wizard's summary rows.
 *   F2 steps     - wizard step circles + "N of M answered" counter +
 *                  Step 1 size echo + sold-out recovery line.
 *   F3 chooser   - inline 2-question chooser, recommend-then-confirm.
 *   F4 confirm   - blank-door confirm before add-to-cart (centred dialog on desktop, bottom sheet on phones).
 *   F5 stickyBar - minimal mobile sticky bar (own DOM; theme cart-bar
 *                  stays OFF - see CONTRACT-js.md for the decision).
 *   F6 trust     - trust-rail live rating/count + AggregateRating JSON-LD.
 *
 * Globals: window.ExosPdpV2 (debug namespace) only.
 * Syntax: ES2017 max. No optional chaining, no nullish coalescing.
 */
(function () {
  "use strict";

  if (window.ExosPdpV2) return;

  var NS = {
    version: "2026-07-13.1",
    features: {}
  };
  window.ExosPdpV2 = NS;

  var REVIEWS_ENDPOINT_DEFAULT = "https://exos-pickup.ggianniotis2.workers.dev";
  var SESSION_KEY = "exosPdpV2BlankConfirm";
  var CHECK = "✓";
  var CROSS = "✗";
  var DOT_SEP = " · ";

  /* ------------------------------------------------------------------ */
  /* Small utilities                                                     */
  /* ------------------------------------------------------------------ */

  function qs(root, sel) {
    return root ? root.querySelector(sel) : null;
  }

  function qsa(root, sel) {
    if (!root) return [];
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function setText(el, text) {
    if (!el) return;
    if (el.textContent !== text) el.textContent = text;
  }

  function addCls(el, cls) {
    if (el && !el.classList.contains(cls)) el.classList.add(cls);
  }

  function removeCls(el, cls) {
    if (el && el.classList.contains(cls)) el.classList.remove(cls);
  }

  function setHiddenAttr(el, hidden) {
    if (!el) return;
    if (hidden && !el.hasAttribute("hidden")) el.setAttribute("hidden", "");
    if (!hidden && el.hasAttribute("hidden")) el.removeAttribute("hidden");
  }

  function prefersReducedMotion() {
    try {
      return Boolean(
        window.matchMedia &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    } catch (e) {
      return false;
    }
  }

  function scrollToEl(el, block) {
    if (!el || typeof el.scrollIntoView !== "function") return;
    /* Instant scroll only, and only when the target is actually out of view.
       Smooth scrolling fought the theme's scroll effects on some devices and
       could leave the page feeling stuck. */
    try {
      var r = el.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      if (r.top >= 0 && r.bottom <= vh) return;
      el.scrollIntoView({ behavior: "auto", block: block || "start" });
    } catch (e) {
      el.scrollIntoView();
    }
  }

  function chipValue(button) {
    if (!button) return "";
    return (
      button.value ||
      button.getAttribute("value") ||
      button.getAttribute("data-choice") ||
      ""
    );
  }

  /* Mirrors asMoney() in door-configurator.js exactly. Used ONLY as a
     fallback formatter when the displayed total string is unavailable. */
  function formatMoneyLikeTheme(cents) {
    if (
      window.theme &&
      typeof window.theme.formatMoney === "function" &&
      window.theme.moneyFormat
    ) {
      if (
        window.theme.settings &&
        window.theme.settings.currency_code_enable &&
        window.theme.moneyWithCurrencyFormat
      ) {
        return window.theme.formatMoney(cents, window.theme.moneyWithCurrencyFormat);
      }
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    return "$" + (cents / 100).toFixed(2);
  }

  /* Parses a displayed money string back to cents (fallback path only). */
  function parseMoneyToCents(text) {
    var t = String(text || "").replace(/[^0-9.,-]/g, "");
    if (!t) return NaN;
    if (t.indexOf(",") > -1 && t.indexOf(".") > -1) {
      t = t.replace(/,/g, "");
    } else if (/,\d{2}$/.test(t) && t.indexOf(".") === -1) {
      t = t.replace(",", ".");
    } else {
      t = t.replace(/,/g, "");
    }
    var n = parseFloat(t);
    return isFinite(n) ? Math.round(n * 100) : NaN;
  }

  /* ------------------------------------------------------------------ */
  /* Shared read-only context (never written to)                         */
  /* ------------------------------------------------------------------ */

  var container = document.querySelector("[data-door-addon-configurator]");
  var productInfo = container ? container.closest("product-info") : null;
  var form = null;
  if (container) {
    var formId = container.getAttribute("data-product-form-id");
    if (formId) form = document.getElementById(formId);
  }

  function getFormWrapper() {
    return productInfo ? qs(productInfo, "[data-form-wrapper]") : null;
  }

  function getSubmitButton() {
    return form ? form.querySelector('button[type="submit"][name="add"]') : null;
  }

  function getSelectedPath() {
    var b = qs(container, "[data-path-option].is-selected");
    return b ? chipValue(b) : "";
  }

  function getChoice(controlName) {
    var b = qs(
      container,
      '[data-control="' + controlName + '"] [data-choice].is-selected'
    );
    return b ? chipValue(b) : "";
  }

  function getInputValue(key) {
    var input = qs(container, '[data-input="' + key + '"]');
    return input ? String(input.value || "").trim() : "";
  }

  function hasHingePhoto() {
    var input = qs(container, "[data-hinge-photo-input]");
    return Boolean(input && input.files && input.files.length > 0);
  }

  function getCheckedSizeInput() {
    var scope = productInfo || document;
    return qs(
      scope,
      ".radio__input[data-single-option-selector]:checked, .swatch__input[data-single-option-selector]:checked"
    );
  }

  function getSizeValue() {
    var input = getCheckedSizeInput();
    return input ? String(input.value || "") : "";
  }

  function hasVariantId() {
    var input = form ? form.querySelector('input[name="id"]') : null;
    return Boolean(input && input.value);
  }

  /* Mirrors custom.js isDoorBuilderReadyToSubmit(). */
  function isSizeReady() {
    return Boolean(getCheckedSizeInput()) && hasVariantId();
  }

  /* Mirrors custom.js isDoorBuilderUnavailable(). */
  function isUnavailable() {
    var wrapper = getFormWrapper();
    if (!wrapper) return false;
    return (
      wrapper.classList.contains("variant--soldout") ||
      wrapper.classList.contains("variant--unavailable")
    );
  }

  function getQuantity() {
    var qtyInput = productInfo ? qs(productInfo, "[data-quantity-field]") : null;
    if (!qtyInput && form) qtyInput = form.querySelector('[name="quantity"]');
    if (!qtyInput) return 1;
    var parsed = parseInt(qtyInput.value, 10);
    if (!isFinite(parsed) || parsed < 1) return 1;
    return parsed;
  }

  function isDoubleDoorSelected() {
    var chips = qsa(container, "[data-choice].is-selected");
    for (var i = 0; i < chips.length; i++) {
      if (chipValue(chips[i]) === "Double Door") return true;
    }
    return false;
  }

  function readAddonProducts() {
    var jsonEl = qs(container, "[data-addon-products]");
    if (!jsonEl) return {};
    try {
      return JSON.parse(jsonEl.textContent);
    } catch (e) {
      return {};
    }
  }

  function readBaseVariantPrices() {
    var jsonEl = qs(container, "[data-base-variant-prices]");
    if (!jsonEl) return {};
    try {
      return JSON.parse(jsonEl.textContent);
    } catch (e) {
      return {};
    }
  }

  /* ------------------------------------------------------------------ */
  /* Refresh pipeline: one observer, many guarded refreshers.            */
  /* A refresher that throws is unregistered (that feature freezes in    */
  /* its last good state; everything else keeps running).                */
  /* ------------------------------------------------------------------ */

  var refreshers = [];
  var refreshScheduled = false;

  function registerRefresher(name, fn) {
    refreshers.push({ name: name, fn: fn });
  }

  function runRefreshers() {
    for (var i = refreshers.length - 1; i >= 0; i--) {
      var entry = refreshers[i];
      try {
        entry.fn();
      } catch (e) {
        NS.features[entry.name] =
          "disabled at runtime: " + (e && e.message ? e.message : String(e));
        refreshers.splice(i, 1);
      }
    }
  }
  NS.refresh = runRefreshers;

  function scheduleRefresh() {
    if (refreshScheduled) return;
    refreshScheduled = true;
    var raf =
      window.requestAnimationFrame ||
      function (cb) {
        setTimeout(cb, 16);
      };
    raf(function () {
      refreshScheduled = false;
      runRefreshers();
    });
  }

  function initSharedPipeline() {
    var observeRoot = productInfo || container;
    if (!observeRoot) return;

    if (window.MutationObserver) {
      var mo = new MutationObserver(scheduleRefresh);
      mo.observe(observeRoot, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["class", "hidden", "disabled"]
      });
    }

    ["change", "input", "click"].forEach(function (evName) {
      observeRoot.addEventListener(evName, scheduleRefresh, true);
    });

    [
      "exos:door-configurator:reset",
      "exos:cart:added",
      "theme:cart:refresh",
      "theme:product-info:loaded"
    ].forEach(function (evName) {
      document.addEventListener(evName, function () {
        scheduleRefresh();
        /* Cover the wizard's reset collapse animation (~520ms) and the
           post-add reset which fires 2s after exos:cart:added. */
        setTimeout(scheduleRefresh, 650);
        setTimeout(scheduleRefresh, 2400);
      });
    });

    if (
      window.subscribe &&
      window.theme &&
      window.theme.PUB_SUB_EVENTS &&
      window.theme.PUB_SUB_EVENTS.variantChange
    ) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, function () {
        scheduleRefresh();
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* F1. Outcome rows                                                    */
  /* ------------------------------------------------------------------ */

  function initOutcome() {
    if (!container) throw new Error("no configurator");
    var pricingBox = qs(container, "[data-addon-pricing]");
    var outcome = qs(container, "[data-exos-outcome]");
    var pricingList = qs(container, "[data-addon-pricing-list]");
    if (!pricingBox || !outcome || !pricingList) {
      throw new Error("missing outcome hooks");
    }

    var rows = {};
    qsa(outcome, "[data-exos-outcome-row]").forEach(function (li) {
      rows[li.getAttribute("data-exos-outcome-row")] = li;
    });
    ["hinge_pockets", "handle_hole", "hinges", "frame"].forEach(function (key) {
      if (!rows[key]) throw new Error("missing outcome row: " + key);
    });
    var outcomeList = rows.frame.parentNode;

    var addonProducts = readAddonProducts();
    var labelToType = {};
    Object.keys(addonProducts).forEach(function (key) {
      var addon = addonProducts[key];
      if (!addon || !addon.label) return;
      var type = "other";
      if (key.indexOf("frame_") === 0) type = "frame";
      else if (key.indexOf("prep_") === 0) type = "prep";
      else if (key.indexOf("hinges_") === 0) type = "hinges";
      else if (key === "custom_height_cutdown") type = "cutdown";
      labelToType[addon.label] = type;
    });

    var DEFAULTS = {
      hinge_pockets: { done: false, status: "not cut", amount: "" },
      handle_hole: { done: false, status: "not cut", amount: "" },
      hinges: { done: false, status: "not included", amount: "" },
      frame: { done: false, status: "not included", amount: "" }
    };

    /* Dynamic rows we may create: the conditional Height row + a generic
       row per unmapped summary line (e.g. "Extra Door Slab (Double Door)")
       so no charge ever disappears when the old rows are hidden. */
    var dynamicRows = {};

    function buildRow(rowKey, labelText) {
      var li = document.createElement("li");
      li.className = "door-addon-configurator__outcome-row";
      li.setAttribute("data-exos-outcome-row", rowKey);

      var x = document.createElement("span");
      x.className =
        "door-addon-configurator__outcome-mark door-addon-configurator__outcome-mark--x";
      x.setAttribute("aria-hidden", "true");
      x.textContent = CROSS;

      var check = document.createElement("span");
      check.className =
        "door-addon-configurator__outcome-mark door-addon-configurator__outcome-mark--check";
      check.setAttribute("aria-hidden", "true");
      check.textContent = CHECK;

      var label = document.createElement("span");
      label.className = "door-addon-configurator__outcome-label";
      label.textContent = labelText;

      var status = document.createElement("span");
      status.className = "door-addon-configurator__outcome-status";
      status.setAttribute("data-exos-outcome-status", "");

      var amount = document.createElement("span");
      amount.className = "door-addon-configurator__outcome-amount";
      amount.setAttribute("data-exos-outcome-amount", "");

      li.appendChild(x);
      li.appendChild(check);
      li.appendChild(label);
      li.appendChild(status);
      li.appendChild(amount);
      return li;
    }

    function writeRow(li, desired) {
      if (!li) return;
      if (desired.done) addCls(li, "is-done");
      else removeCls(li, "is-done");
      setText(qs(li, "[data-exos-outcome-status]"), desired.status);
      setText(qs(li, "[data-exos-outcome-amount]"), desired.amount);
    }

    /* Reads the wizard-rendered summary rows verbatim:
       <li><span>LABEL[ xN]</span><strong>$X</strong></li> */
    function readSummaryRows() {
      return qsa(pricingList, "li").map(function (li) {
        var labelEl = li.querySelector("span");
        var amountEl = li.querySelector("strong");
        var raw = labelEl ? labelEl.textContent : "";
        var label = raw.replace(/\s*x\d+\s*$/, "").trim();
        var type = labelToType[label];
        if (!type) {
          type = label.indexOf("Extra Door Slab") === 0 ? "extra" : "other";
        }
        return {
          label: label,
          amount: amountEl ? amountEl.textContent.trim() : "",
          type: type
        };
      });
    }

    function frameStatusFromLabel(label) {
      /* 'FJ Pine + 4 5/8"' -> 'Pine, 4 5/8"'; 'MDF + 4 5/8"' -> 'MDF, 4 5/8"' */
      return label.replace(/^FJ\s+/, "").replace(" + ", ", ");
    }

    function hingesStatusFromLabel(label) {
      /* 'Black Hinges' -> 'black'. Count deliberately omitted: the engine
         charges one hinge product unit per door and the per-set count is
         not verifiable from the display, so a number could lie. */
      return label.replace(/\s*Hinges\s*$/i, "").toLowerCase() || "included";
    }

    function activeHeightInputKey(path) {
      if (path === "frame_set") return "frame_custom_height_value";
      if (path === "door_prep") return "prep_custom_height_value";
      if (path === "custom_height") return "only_custom_height_value";
      return "";
    }

    function syncDynamicRow(rowKey, labelText, desired, present) {
      if (present) {
        if (!dynamicRows[rowKey]) {
          dynamicRows[rowKey] = buildRow(rowKey, labelText);
          outcomeList.appendChild(dynamicRows[rowKey]);
        }
        writeRow(dynamicRows[rowKey], desired);
      } else if (dynamicRows[rowKey]) {
        if (dynamicRows[rowKey].parentNode) {
          dynamicRows[rowKey].parentNode.removeChild(dynamicRows[rowKey]);
        }
        delete dynamicRows[rowKey];
      }
    }

    function apply() {
      var path = getSelectedPath();
      var summary = readSummaryRows();
      var byType = {};
      var extras = [];
      summary.forEach(function (row) {
        if (
          row.type === "frame" ||
          row.type === "prep" ||
          row.type === "hinges" ||
          row.type === "cutdown"
        ) {
          if (!byType[row.type]) byType[row.type] = row;
        } else {
          extras.push(row);
        }
      });

      var desired = {
        hinge_pockets: DEFAULTS.hinge_pockets,
        handle_hole: DEFAULTS.handle_hole,
        hinges: DEFAULTS.hinges,
        frame: DEFAULTS.frame
      };

      if (path === "frame_set") {
        if (byType.prep) {
          /* Frame sets always include standard prep (hinge pockets). */
          desired.hinge_pockets = {
            done: true,
            status: "our standard spots",
            amount: byType.prep.amount
          };
        }
        if (getChoice("frame_lock_prep") === "yes") {
          desired.handle_hole = { done: true, status: "included", amount: "" };
        }
        if (byType.frame) {
          desired.frame = {
            done: true,
            status: frameStatusFromLabel(byType.frame.label),
            amount: byType.frame.amount
          };
        }
        if (byType.hinges) {
          desired.hinges = {
            done: true,
            status: hingesStatusFromLabel(byType.hinges.label),
            amount: byType.hinges.amount
          };
        }
      } else if (path === "door_prep") {
        desired.frame = {
          done: true,
          status: "using your existing frame",
          amount: ""
        };

        var hingeToggle = getChoice("prep_hinge_toggle");
        var hingeType = getChoice("prep_hinge_type");
        var lockToggle = getChoice("prep_lock_toggle");
        var lockType = getChoice("prep_lock_type");

        if (byType.prep && hingeToggle === "yes" && hingeType) {
          /* One prep charge covers hinge + lock; the money lives on the
             Hinge pockets row and the Handle hole row reads "included". */
          desired.hinge_pockets = {
            done: true,
            status: hingeType === "custom" ? "custom spots" : "our standard spots",
            amount: byType.prep.amount
          };
          if (lockToggle === "yes" && lockType) {
            desired.handle_hole = { done: true, status: "included", amount: "" };
          }
        } else if (byType.prep && lockToggle === "yes" && lockType) {
          /* Only the handle hole is being cut: the charge lives here. */
          desired.handle_hole = {
            done: true,
            status: lockType === "custom" ? "your custom spot" : "our standard spot",
            amount: byType.prep.amount
          };
        }

        if (byType.hinges) {
          desired.hinges = {
            done: true,
            status: hingesStatusFromLabel(byType.hinges.label),
            amount: byType.hinges.amount
          };
        }
      }
      /* path === "custom_height" or none: the four base rows stay at
         their honest defaults (all crossed). */

      writeRow(rows.hinge_pockets, desired.hinge_pockets);
      writeRow(rows.handle_hole, desired.handle_hole);
      writeRow(rows.hinges, desired.hinges);
      writeRow(rows.frame, desired.frame);

      /* Conditional Height row. */
      var cut = byType.cutdown;
      var heightDesired = null;
      if (cut) {
        var heightValue = getInputValue(activeHeightInputKey(path)).replace(/["']/g, "").trim();
        heightDesired = {
          done: true,
          status: heightValue
            ? "cut to " + heightValue + " inches"
            : "cut shorter than 80 inches",
          amount: cut.amount
        };
      }
      syncDynamicRow("height", "Height", heightDesired || DEFAULTS.frame, Boolean(cut));

      /* Generic rows for anything the summary shows that we do not map
         (e.g. Extra Door Slab). Never guess a number, mirror verbatim. */
      var extraKeys = {};
      extras.forEach(function (row, index) {
        var rowKey = "extra_" + index;
        extraKeys[rowKey] = true;
        if (!dynamicRows[rowKey]) {
          dynamicRows[rowKey] = buildRow(rowKey, row.label);
          outcomeList.appendChild(dynamicRows[rowKey]);
        } else {
          setText(
            qs(dynamicRows[rowKey], ".door-addon-configurator__outcome-label"),
            row.label
          );
        }
        writeRow(dynamicRows[rowKey], {
          done: true,
          status: "included",
          amount: row.amount
        });
      });
      Object.keys(dynamicRows).forEach(function (rowKey) {
        if (rowKey.indexOf("extra_") === 0 && !extraKeys[rowKey]) {
          syncDynamicRow(rowKey, "", DEFAULTS.frame, false);
        }
      });

      /* Outcome block is live: hide the old duplicate rows (title, list,
         empty line). The Options total row stays visible and untouched. */
      addCls(pricingBox, "exos-outcome-live");
    }

    registerRefresher("outcome", apply);
    apply();
  }

  /* ------------------------------------------------------------------ */
  /* F2. Step completion + counter + size echo + sold-out recovery       */
  /* ------------------------------------------------------------------ */

  function initSteps() {
    if (!container) throw new Error("no configurator");

    var circles = qsa(container, "[data-exos-step-circle]");

    /* "answered" predicates. A Yes with an unanswered sub-choice stays
       incomplete. These read the same .is-selected chips and input values
       the wizard maintains; they never write to them. */
    var COMPLETE = {
      frame_type: function () {
        return Boolean(getChoice("frame_type"));
      },
      jamb_size: function () {
        return Boolean(getChoice("jamb_size"));
      },
      frame_lock_prep: function () {
        return Boolean(getChoice("frame_lock_prep"));
      },
      frame_swing: function () {
        return Boolean(getChoice("frame_swing"));
      },
      frame_hinges: function () {
        var v = getChoice("frame_hinges");
        if (!v) return false;
        return v !== "yes" || Boolean(getChoice("frame_hinge_finish"));
      },
      frame_custom_height_toggle: function () {
        var v = getChoice("frame_custom_height_toggle");
        if (!v) return false;
        return v !== "yes" || Boolean(getInputValue("frame_custom_height_value"));
      },
      prep_hinge_toggle: function () {
        var v = getChoice("prep_hinge_toggle");
        if (!v) return false;
        if (v !== "yes") return true;
        var type = getChoice("prep_hinge_type");
        if (!type) return false;
        if (type !== "custom") return true;
        return (
          Boolean(getInputValue("top_hinge_top")) &&
          Boolean(getInputValue("bottom_hinge_top")) &&
          hasHingePhoto()
        );
      },
      prep_lock_toggle: function () {
        var v = getChoice("prep_lock_toggle");
        if (!v) return false;
        if (v !== "yes") return true;
        var type = getChoice("prep_lock_type");
        if (!type) return false;
        if (type !== "custom") return true;
        return Boolean(getInputValue("lock_centre_location"));
      },
      prep_swing: function () {
        return Boolean(getChoice("prep_swing"));
      },
      prep_hinges: function () {
        var v = getChoice("prep_hinges");
        if (!v) return false;
        return v !== "yes" || Boolean(getChoice("prep_hinge_finish"));
      },
      prep_custom_height_toggle: function () {
        var v = getChoice("prep_custom_height_toggle");
        if (!v) return false;
        return v !== "yes" || Boolean(getInputValue("prep_custom_height_value"));
      }
    };

    /* Counter element kept detached: owner removed the "N of M answered"
       line (2026-07-13, redundant next to the step checkmarks). The update
       logic below still writes to it harmlessly. */
    var counter = document.createElement("p");
    counter.className = "exos-steps-count";
    counter.setAttribute("data-exos-steps-count", "");
    counter.setAttribute("hidden", "");

    function apply() {
      var path = getSelectedPath();
      var visibleCount = 0;
      var doneCount = 0;

      circles.forEach(function (circle) {
        var field = circle.closest("[data-field]");
        var workflow = circle.closest("[data-workflow]");
        var isVisible = Boolean(
          field &&
            workflow &&
            !field.classList.contains("is-hidden") &&
            !workflow.classList.contains("is-hidden")
        );
        if (!circle.hasAttribute("data-exos-num")) {
          circle.setAttribute("data-exos-num", circle.textContent);
        }
        var name = circle.getAttribute("data-exos-step-circle");
        var predicate = COMPLETE[name];
        var complete = Boolean(isVisible && predicate && predicate());

        if (complete) {
          addCls(circle, "is-done");
          setText(circle, CHECK);
        } else {
          removeCls(circle, "is-done");
          setText(circle, circle.getAttribute("data-exos-num"));
        }

        if (isVisible) {
          visibleCount += 1;
          if (complete) doneCount += 1;
        }
      });

      if (!path || visibleCount === 0) {
        setHiddenAttr(counter, true);
      } else {
        setText(counter, doneCount + " of " + visibleCount + " answered");
        setHiddenAttr(counter, false);
      }

      /* Size echo + sold-out recovery line. Re-queried every pass because
         product-info.js replaces the whole <variant-selects> element on
         every variant change, wiping anything we wrote into it. */
      var scope = productInfo || document;
      var sizeValue = getSizeValue();

      var echoEl = qs(scope, "[data-exos-size-echo]");
      if (echoEl) {
        setText(echoEl, sizeValue ? CHECK + " " + sizeValue : "");
      }

      var oosEl = qs(scope, "[data-exos-oos]");
      if (oosEl) {
        var checked = getCheckedSizeInput();
        var pill = checked ? checked.closest(".radio__button") : null;
        var pillSoldOut = Boolean(pill && pill.classList.contains("sold-out"));
        var show = Boolean(sizeValue) && (pillSoldOut || isUnavailable());
        var oosSizeEl = qs(oosEl, "[data-exos-oos-size]");
        if (show && oosSizeEl) setText(oosSizeEl, sizeValue);
        setHiddenAttr(oosEl, !show);
      }
    }

    registerRefresher("steps", apply);
    apply();
  }

  /* ------------------------------------------------------------------ */
  /* F3. Inline 2-question chooser (recommend-then-confirm, never        */
  /*     auto-select)                                                    */
  /* ------------------------------------------------------------------ */

  function initChooser() {
    if (!container) throw new Error("no configurator");
    var toggle = qs(container, "[data-exos-chooser-toggle]");
    var panel = qs(container, "[data-exos-chooser]");
    if (!toggle || !panel) throw new Error("missing chooser hooks");

    var q1Chips = qsa(panel, "[data-exos-chooser-q1]");
    var q2Chips = qsa(panel, "[data-exos-chooser-q2]");
    if (!q1Chips.length || !q2Chips.length) throw new Error("missing chooser chips");

    /* Q2 block = the question <p> right before the Q2 row + the row. */
    var q2Row = q2Chips[0].parentNode;
    var q2Question = q2Row ? q2Row.previousElementSibling : null;
    var q2Block = [];
    if (q2Question) q2Block.push(q2Question);
    if (q2Row) q2Block.push(q2Row);

    function hideQ2() {
      q2Block.forEach(function (el) {
        addCls(el, "is-hidden");
      });
      q2Chips.forEach(function (chip) {
        removeCls(chip, "is-selected");
      });
    }

    function showQ2() {
      q2Block.forEach(function (el) {
        removeCls(el, "is-hidden");
      });
    }

    hideQ2();

    toggle.addEventListener("click", function () {
      var nowHidden = panel.classList.toggle("is-hidden");
      toggle.setAttribute("aria-expanded", nowHidden ? "false" : "true");
    });

    function clearRecs() {
      qsa(container, "[data-exos-rec]").forEach(function (rec) {
        addCls(rec, "is-hidden");
      });
    }

    function cardTitle(target) {
      /* Text nodes of the title span only (skips the check/tag/rec spans). */
      var titleEl = qs(target, ".door-addon-configurator__situation-title");
      if (!titleEl) return "";
      var out = "";
      for (var i = 0; i < titleEl.childNodes.length; i++) {
        var n = titleEl.childNodes[i];
        if (n.nodeType === 3) out += n.textContent;
      }
      return out.replace(/\s+/g, " ").trim();
    }

    var resultRow = null;
    function clearResultRow() {
      if (resultRow && resultRow.parentNode) resultRow.parentNode.removeChild(resultRow);
      resultRow = null;
    }

    function showResultRow(target) {
      clearResultRow();
      var title = cardTitle(target) || "the highlighted option above";
      resultRow = document.createElement("div");
      resultRow.className = "exos-chooser-result";
      var label = document.createElement("p");
      label.className = "exos-chooser-result__label";
      label.textContent = "Based on your answers, this is the option for you:";
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "exos-chooser-result__btn";
      btn.textContent = title;
      btn.addEventListener("click", function () {
        target.click();
      });
      resultRow.appendChild(label);
      resultRow.appendChild(btn);
      panel.appendChild(resultRow);
    }

    function recommend(pathValue) {
      var target = qs(container, '[data-path-option][value="' + pathValue + '"]');
      if (!target) return;
      clearRecs();
      var rec = qs(target, "[data-exos-rec]");
      if (rec) removeCls(rec, "is-hidden");
      showResultRow(target);
      scrollToEl(target, "center");
    }

    function selectChip(chips, chip) {
      chips.forEach(function (c) {
        if (c === chip) addCls(c, "is-selected");
        else removeCls(c, "is-selected");
      });
    }

    function openFrameHelp() {
      /* Always the standalone page in a new tab. Opening the in-page drawer
         from here engaged the theme's scroll lock while the drawer itself sat
         inside a hidden wizard step: invisible drawer, locked page. */
      window.open("/pages/what-is-frame-type", "_blank", "noopener");
    }

    q1Chips.forEach(function (chip) {
      chip.addEventListener("click", function () {
        var answer = chip.getAttribute("data-exos-chooser-q1");
        if (answer === "unsure") {
          /* Info action, not an answer: open the explainer and leave the
             question exactly as it was. */
          openFrameHelp();
          return;
        }
        selectChip(q1Chips, chip);
        if (answer === "keep") {
          clearRecs();
          clearResultRow();
          showQ2();
        } else if (answer === "replace") {
          hideQ2();
          recommend("frame_set");
        }
      });
    });

    q2Chips.forEach(function (chip) {
      chip.addEventListener("click", function () {
        selectChip(q2Chips, chip);
        var answer = chip.getAttribute("data-exos-chooser-q2");
        if (answer === "cut") {
          recommend("door_prep");
        } else if (answer === "shorter") {
          recommend("custom_height");
        }
      });
    });

    /* Recommendation retires the moment a real path is picked (the
       selected-card CSS then owns the visual state). */
    container.addEventListener("click", function (event) {
      if (!event.target || !event.target.closest) return;
      if (event.target.closest("[data-path-option]")) {
        clearRecs();
        clearResultRow();
        addCls(panel, "is-hidden");
        toggle.setAttribute("aria-expanded", "false");
        /* A choice is made: the skip line and not-sure link have served
           their purpose. Start over brings them back. */
        addCls(container, "exos-fork-tidy");
      } else if (event.target.closest("[data-path-reset]")) {
        removeCls(container, "exos-fork-tidy");
      }
    });

    document.addEventListener("exos:door-configurator:reset", function () {
      clearRecs();
      clearResultRow();
      removeCls(container, "exos-fork-tidy");
    });
  }

  /* ------------------------------------------------------------------ */
  /* F4. Blank-door confirm (centred dialog desktop, bottom sheet phone)  */
  /* ------------------------------------------------------------------ */

  function initConfirm() {
    if (!container || !form) throw new Error("no configurator/form");

    var memoryFlag = "";
    function getFlag() {
      try {
        return sessionStorage.getItem(SESSION_KEY) || memoryFlag;
      } catch (e) {
        return memoryFlag;
      }
    }
    function setFlag(value) {
      memoryFlag = value;
      try {
        sessionStorage.setItem(SESSION_KEY, value);
      } catch (e) {
        /* private mode: memory flag still applies for this page view */
      }
    }

    var sheet = null;
    var panelEl = null;
    var primaryBtn = null;
    var anywayBtn = null;
    var lastFocus = null;
    var originBtn = null;
    var prevBodyOverflow = "";
    var keydownHandler = null;

    function outcomeRowHtml(label, status) {
      return (
        '<li class="door-addon-configurator__outcome-row">' +
        '<span class="door-addon-configurator__outcome-mark door-addon-configurator__outcome-mark--x" aria-hidden="true">' +
        CROSS +
        "</span>" +
        '<span class="door-addon-configurator__outcome-label">' +
        label +
        "</span>" +
        '<span class="door-addon-configurator__outcome-status">' +
        status +
        "</span>" +
        "</li>"
      );
    }

    function buildSheet() {
      if (sheet) return;
      sheet = document.createElement("div");
      sheet.className = "exos-sheet";
      sheet.setAttribute("data-exos-sheet", "");
      sheet.setAttribute("hidden", "");
      sheet.innerHTML =
        '<div class="exos-sheet__backdrop" data-exos-sheet-close></div>' +
        '<div class="exos-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="exos-sheet-title">' +
        '<h3 class="exos-sheet__title" id="exos-sheet-title">Quick check before you add this</h3>' +
        '<p class="exos-sheet__body">You have not picked any fitting options. This door will arrive blank:</p>' +
        '<ul class="exos-sheet__rows door-addon-configurator__outcome-list">' +
        outcomeRowHtml("Hinge pockets", "not cut") +
        outcomeRowHtml("Handle hole", "not cut") +
        outcomeRowHtml("Hinges", "not included") +
        outcomeRowHtml("Frame", "not included") +
        "</ul>" +
        '<p class="exos-sheet__caution">Hanging a blank door takes woodworking tools and know-how.</p>' +
        '<div class="exos-sheet__actions">' +
        '<button type="button" class="exos-sheet__anyway" data-exos-sheet-anyway>I just want the blank door</button>' +
        '<button type="button" class="exos-sheet__primary" data-exos-sheet-primary>Pick fitting options</button>' +
        "</div>" +
        "</div>";
      document.body.appendChild(sheet);

      panelEl = qs(sheet, ".exos-sheet__panel");
      primaryBtn = qs(sheet, "[data-exos-sheet-primary]");
      anywayBtn = qs(sheet, "[data-exos-sheet-anyway]");

      qs(sheet, "[data-exos-sheet-close]").addEventListener("click", function () {
        closeSheet(true);
      });

      primaryBtn.addEventListener("click", function () {
        closeSheet(false);
        scrollToEl(container, "start");
      });

      anywayBtn.addEventListener("click", function () {
        setFlag("bypass");
        var btn = originBtn;
        closeSheet(false);
        setTimeout(function () {
          if (btn && document.body.contains(btn)) btn.click();
        }, 0);
      });
    }

    function openSheet() {
      buildSheet();
      setFlag("shown");
      lastFocus = document.activeElement;
      setHiddenAttr(sheet, false);
      prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      try {
        document.dispatchEvent(
          new CustomEvent("exos:pdpv2:sheet-open", { bubbles: true })
        );
      } catch (e) {
        /* older browsers without CustomEvent constructor: no bar coupling */
      }

      keydownHandler = function (event) {
        if (event.key === "Escape" || event.key === "Esc") {
          event.preventDefault();
          closeSheet(true);
          return;
        }
        if (event.key === "Tab") {
          var focusables = [anywayBtn, primaryBtn];
          var index = focusables.indexOf(document.activeElement);
          if (event.shiftKey) {
            event.preventDefault();
            var prev = index <= 0 ? focusables[focusables.length - 1] : focusables[index - 1];
            prev.focus();
          } else {
            event.preventDefault();
            var next = index === -1 || index === focusables.length - 1 ? focusables[0] : focusables[index + 1];
            next.focus();
          }
        }
      };
      document.addEventListener("keydown", keydownHandler, true);
      primaryBtn.focus();
    }

    function closeSheet(restoreFocus) {
      if (!sheet) return;
      setHiddenAttr(sheet, true);
      document.body.style.overflow = prevBodyOverflow;
      if (keydownHandler) {
        document.removeEventListener("keydown", keydownHandler, true);
        keydownHandler = null;
      }
      try {
        document.dispatchEvent(
          new CustomEvent("exos:pdpv2:sheet-close", { bubbles: true })
        );
      } catch (e) {
        /* no-op */
      }
      if (restoreFocus && lastFocus && document.body.contains(lastFocus)) {
        try {
          lastFocus.focus();
        } catch (e) {
          /* no-op */
        }
      }
      lastFocus = null;
    }

    function belongsToThisForm(btn) {
      if (btn.hasAttribute("data-cart-bar-add-to-cart")) {
        /* Theme cart-bar button (only exists if show_cart_bar is enabled
           later): accept it when it lives in the same product-info. */
        var pi = btn.closest("product-info");
        return Boolean(productInfo && pi === productInfo);
      }
      if (btn.form === form) return true;
      return form.contains(btn);
    }

    /* Capture phase on document: runs before the theme's own handlers.
       Failing open is mandatory: any exception must not block the click. */
    document.addEventListener(
      "click",
      function (event) {
        var intercepted = false;
        try {
          if (getFlag()) return;
          var target = event.target;
          if (!target || !target.closest) return;
          var btn = target.closest(
            'button[name="add"], [data-cart-bar-add-to-cart]'
          );
          if (!btn || btn.disabled) return;
          if (!belongsToThisForm(btn)) return;
          if (getSelectedPath()) return; /* a path is picked: wizard flow */
          if (!isSizeReady()) return; /* let the size gating message run */
          if (isUnavailable()) return; /* sold out: native handling */

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          intercepted = true;
          originBtn = btn;
          openSheet();
        } catch (e) {
          NS.features.confirm =
            "disabled at runtime: " + (e && e.message ? e.message : String(e));
          if (intercepted && originBtn) {
            /* We already swallowed the click: replay it untouched. */
            setFlag("bypass");
            var btnRef = originBtn;
            setTimeout(function () {
              if (btnRef && document.body.contains(btnRef)) btnRef.click();
            }, 0);
          }
        }
      },
      true
    );
  }

  /* ------------------------------------------------------------------ */
  /* F5. Mobile sticky bar (own minimal bar; theme cart-bar stays off)   */
  /* ------------------------------------------------------------------ */

  function initStickyBar() {
    if (!container || !form) throw new Error("no configurator/form");

    var bar = document.createElement("div");
    bar.className = "exos-sticky";
    bar.setAttribute("data-exos-sticky", "");
    bar.innerHTML =
      '<button type="button" class="exos-sticky__info" data-exos-sticky-info>' +
      '<span class="exos-sticky__line1">' +
      '<span class="exos-sticky__dot" data-exos-sticky-dot hidden></span>' +
      '<span data-exos-sticky-l1></span>' +
      "</span>" +
      '<span class="exos-sticky__line2" data-exos-sticky-l2></span>' +
      "</button>" +
      '<button type="button" class="exos-sticky__atc" data-exos-sticky-atc>Add to cart</button>';
    document.body.appendChild(bar);

    var l1 = qs(bar, "[data-exos-sticky-l1]");
    var l2 = qs(bar, "[data-exos-sticky-l2]");
    var dot = qs(bar, "[data-exos-sticky-dot]");
    var infoBtn = qs(bar, "[data-exos-sticky-info]");
    var atcBtn = qs(bar, "[data-exos-sticky-atc]");
    var pricingList = qs(container, "[data-addon-pricing-list]");
    var pricingTotal = qs(container, "[data-addon-pricing-total]");
    var basePrices = readBaseVariantPrices();

    /* The bar's Add to Cart MUST delegate by clicking the real button so
       the size gate (custom.js), the blank-door confirm (F4) and the
       configurator's submit interception all run identically. */
    atcBtn.addEventListener("click", function () {
      var real = getSubmitButton();
      if (!real || real.disabled) return;
      real.click();
    });

    infoBtn.addEventListener("click", function () {
      if (!isSizeReady()) {
        var scope = productInfo || document;
        var grid =
          qs(scope, ".exos-step1") ||
          qs(scope, "variant-selects") ||
          form;
        scrollToEl(grid, "start");
      } else {
        scrollToEl(container, "start");
      }
    });

    function getDisplayedTotal() {
      /* Primary: the exact total string the wizard writes into the real
         Add to Cart button (base price x qty + options, already themed
         money formatting). */
      var real = getSubmitButton();
      var span = real ? real.querySelector("[data-product-price]") : null;
      if (span) {
        var text = span.textContent.trim();
        if (text) return text;
      }
      /* Fallback: base variant price (same JSON the wizard reads) x qty
         plus the displayed Options total, formatted the same way. */
      try {
        var input = form.querySelector('input[name="id"]');
        var variantId = input ? String(input.value || "") : "";
        var base = 0;
        if (variantId && Object.prototype.hasOwnProperty.call(basePrices, variantId)) {
          base = parseInt(basePrices[variantId], 10) || 0;
        }
        var options = pricingTotal ? parseMoneyToCents(pricingTotal.textContent) : 0;
        if (!isFinite(options)) options = 0;
        return formatMoneyLikeTheme(base * getQuantity() + options);
      } catch (e) {
        return "";
      }
    }

    function apply() {
      var sizeValue = getSizeValue();
      var optionRows = pricingList ? pricingList.children.length : 0;
      var qty = getQuantity();
      var doors = isDoubleDoorSelected() ? qty * 2 : qty;

      if (!sizeValue) {
        setText(l1, "Pick a size above");
        setHiddenAttr(dot, true);
        setText(l2, "");
        setHiddenAttr(l2, true);
      } else {
        if (optionRows === 0) {
          setText(l1, sizeValue + DOT_SEP + "blank door");
          setHiddenAttr(dot, false);
        } else {
          setText(
            l1,
            sizeValue +
              DOT_SEP +
              optionRows +
              (optionRows === 1 ? " option" : " options")
          );
          setHiddenAttr(dot, true);
        }
        var total = getDisplayedTotal();
        setText(
          l2,
          doors + (doors === 1 ? " door" : " doors") + (total ? DOT_SEP + total : "")
        );
        setHiddenAttr(l2, false);
      }

      var unavailable = isUnavailable();
      setText(atcBtn, unavailable ? "Sold out" : "Add to cart");
      var real = getSubmitButton();
      var shouldDisable = unavailable || Boolean(real && real.disabled);
      if (atcBtn.disabled !== shouldDisable) atcBtn.disabled = shouldDisable;
    }

    registerRefresher("stickyBar", apply);
    apply();

    /* Visibility: same pattern as the theme cart-bar - appears once the
       buy form is scrolled past above the viewport. */
    var wrapper = getFormWrapper() || form;
    if (window.IntersectionObserver && wrapper) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            if (show) addCls(bar, "is-visible");
            else removeCls(bar, "is-visible");
          });
        },
        { rootMargin: "0px", threshold: 0 }
      );
      io.observe(wrapper);
    } else if (wrapper) {
      var onScroll = function () {
        var rect = wrapper.getBoundingClientRect();
        if (rect.bottom < 0) addCls(bar, "is-visible");
        else removeCls(bar, "is-visible");
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
    }

    /* Never sit on top of the confirm sheet. */
    document.addEventListener("exos:pdpv2:sheet-open", function () {
      addCls(bar, "exos-sticky--suppressed");
    });
    document.addEventListener("exos:pdpv2:sheet-close", function () {
      removeCls(bar, "exos-sticky--suppressed");
    });
  }

  /* ------------------------------------------------------------------ */
  /* F6. Trust rail + AggregateRating JSON-LD                            */
  /* ------------------------------------------------------------------ */

  function initTrust() {
    if (!window.fetch) throw new Error("no fetch");

    var rail = document.querySelector("[data-exos-trustrail]");
    var endpoint = REVIEWS_ENDPOINT_DEFAULT;
    if (rail && rail.getAttribute("data-exos-reviews-endpoint")) {
      endpoint = rail.getAttribute("data-exos-reviews-endpoint");
    }
    endpoint = String(endpoint).replace(/\/+$/, "");

    function productName() {
      var h1 = document.querySelector(".product__title");
      if (h1) {
        var text = h1.textContent.trim();
        if (text) return text;
      }
      /* Fall back to the document title, trimmed at common separators. */
      var title = document.title || "";
      var parts = title.split(/\s+[|\u2013\u2014-]\s+/);
      return (parts[0] || title).trim();
    }

    function injectJsonLd(average, count) {
      if (document.getElementById("exos-pdp-v2-jsonld")) return;
      var canonical = document.querySelector('link[rel="canonical"]');
      var url =
        canonical && canonical.href
          ? canonical.href
          : window.location.origin + window.location.pathname;
      var name = productName();
      if (!name) return;
      /* The theme already emits {{ product | structured_data }} (a Product
         entity without aggregateRating and without @id). We emit ONE
         additional Product node carrying @id = canonical URL so consumers
         can connect the two; numbers come ONLY from the live feed. */
      var payload = {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": url,
        name: name,
        url: url,
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: Number(average.toFixed(1)),
          reviewCount: count,
          bestRating: 5,
          worstRating: 1
        }
      };
      var script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "exos-pdp-v2-jsonld";
      script.textContent = JSON.stringify(payload);
      document.head.appendChild(script);
    }

    /* Same request + same aggregate source as sections/exos-reviews-strip:
       fetch {endpoint}/reviews/approved and read data.average/data.count
       straight from the response (the min-rating filter applies to the
       displayed cards only, never to the aggregate). */
    fetch(endpoint + "/reviews/approved")
      .then(function (response) {
        if (!response.ok) throw new Error("feed http error");
        return response.json();
      })
      .then(function (data) {
        try {
          if (!data || !data.count) return;
          var average = data.average;
          if (typeof average !== "number" || !isFinite(average)) return;
          var count = parseInt(data.count, 10);
          if (!isFinite(count) || count < 1) return;

          var ratingEl = document.querySelector("[data-exos-rating]");
          var countEl = document.querySelector("[data-exos-count]");
          if (ratingEl) setText(ratingEl, average.toFixed(1));
          if (countEl) setText(countEl, String(count));

          injectJsonLd(average, count);
        } catch (e) {
          NS.features.trust =
            "disabled at runtime: " + (e && e.message ? e.message : String(e));
        }
      })
      .catch(function () {
        /* Fetch failed: rail keeps its static fallback text and no
           JSON-LD is injected. */
      });
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  function safeInit(name, fn) {
    try {
      fn();
      NS.features[name] = "ok";
    } catch (e) {
      NS.features[name] =
        "disabled: " + (e && e.message ? e.message : String(e));
    }
  }

  function boot() {
    safeInit("pipeline", initSharedPipeline);
    safeInit("outcome", initOutcome);
    safeInit("steps", initSteps);
    safeInit("chooser", initChooser);
    safeInit("confirm", initConfirm);
    safeInit("stickyBar", initStickyBar);
    safeInit("trust", initTrust);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
